// SignupFlow.js - the "Get your website / 7-day free trial" sign-up, shared by the Ownezzy site (apps/ui) and the
// demo stores (stores/plants). Source: shared/signup/. Each app builds from its own folder, so each keeps a copy in
// src/shared/signup/ made by scripts/sync-shared-signup.sh: edit this source, then run the script. Never edit a copy.
//
// Steps: name the store (live address check) -> WhatsApp number and email -> email code -> plan and address (free
// trial by default; a paid plan adds a main domain, coupon and payment (UPI, cards and more)) -> ready (login details).
// Plain React and fetch only, so it works in both apps. Events go to onTrack(type, label, meta) with the funnel event
// names the analytics backend accepts (models/VisitorActivity.js).

import React, { useEffect, useRef, useState } from 'react';
import './SignupFlow.css';
import { useUsd, isInternational } from './usd';

const DEFAULT_LINKS = {
  terms: 'https://ownezzy.com/terms',
  privacy: 'https://ownezzy.com/privacy',
  refund: 'https://ownezzy.com/refund-policy',
  plans: 'https://ownezzy.com/#plans',
};

const EXISTING_PASSWORD = 'Your existing Ownezzy password (or log in with a code sent to your email)';
const DOMAIN_RE = /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

const money = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

// The same rule as the backend (apps/backend/utils/slug.js), so the address shown is the address created
export const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

// fetch with JSON in and out; a failed request throws an Error with the server's message and its status
async function api(base, path, { method = 'GET', body, headers = {} } = {}) {
  let res;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...headers },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Can't reach Ownezzy right now. Check your connection and try again.");
  }
  let data = null;
  if ((res.headers.get('content-type') || '').includes('application/json')) {
    data = await res.json().catch(() => null);
  }
  if (!res.ok) {
    const err = new Error((data && data.message) || (res.status >= 500 ? 'Something went wrong on our side. Please try again.' : 'Something went wrong. Please try again.'));
    err.status = res.status;
    throw err;
  }
  return data || {};
}

// Razorpay Checkout on Ownezzy's account, UPI only (the UPI apps on a phone, a QR code on a computer)
const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
let razorpayLoading = null;
const loadRazorpay = () => {
  if (window.Razorpay) return Promise.resolve();
  if (!razorpayLoading) {
    razorpayLoading = new Promise((resolve, reject) => {
      const tag = document.createElement('script');
      tag.src = RAZORPAY_SCRIPT;
      tag.async = true;
      tag.onload = resolve;
      tag.onerror = () => {
        razorpayLoading = null;
        reject(new Error('The payment window could not be loaded. Check your connection and try again.'));
      };
      document.body.appendChild(tag);
    });
  }
  return razorpayLoading;
};

async function openRazorpay(checkout, { prefill, onPaid, onClose }) {
  await loadRazorpay();
  const rzp = new window.Razorpay({
    key: checkout.keyId,
    order_id: checkout.providerOrderId,
    amount: checkout.amountPaise,
    currency: 'INR',
    name: checkout.name,
    description: checkout.description,
    prefill,
    config: {
      display: {
        // UPI first, then cards, net banking and wallets; outside India cards, net banking and wallets come first, as UPI needs an Indian bank account
        blocks: { first: isInternational() ? { name: 'Card, net banking or wallet', instruments: [{ method: 'card' }, { method: 'netbanking' }, { method: 'wallet' }] } : { name: 'Pay by UPI', instruments: [{ method: 'upi' }] } },
        sequence: ['block.first'],
        preferences: { show_default_blocks: true },
      },
    },
    handler: (res) => onPaid({ paymentId: res.razorpay_payment_id, signature: res.razorpay_signature }),
    modal: { ondismiss: onClose, confirm_close: true },
  });
  rzp.open();
}

// "Resend code" wait: start() after a code is sent; left counts down to 0
function useCountdown(seconds) {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!until) return undefined;
    const timer = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [until]);
  return {
    left: until ? Math.max(0, Math.ceil((until - now) / 1000)) : 0,
    start: () => {
      setNow(Date.now());
      setUntil(Date.now() + seconds * 1000);
    },
  };
}

const CONFETTI_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#a855f7', '#14b8a6'];
const CONFETTI = Array.from({ length: 24 }, (_, i) => ({
  left: `${(i * 37) % 100}%`,
  background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  animationDelay: `${(i % 6) * 0.08}s`,
  '--drift': `${((i % 5) - 2) * 26}px`,
}));

const Spinner = () => <span className="ozsf-spinner" aria-hidden="true" />;

export default function SignupFlow({
  open,
  onClose,
  template = '',
  layout = '', // 'v2': the store starts in the Studio design (signed up from a v2 demo)
  currency = '', // a store abroad: its currency and country, from the international demo the sign-up started on
  country = '',
  category = '',
  design = null, // { name, icon, label, demoUrl }: the demo design the store starts from
  categoryOptions = null, // [{ name, icon, template }]: shows a business type field when there is no design
  resolveTemplate = null, // (category, template) => template key, for a typed business type
  initialPlan = '',
  referral = '',
  logo = '',
  links = {},
  apiBase = '/api',
  onTrack = () => {},
}) {
  const urls = { ...DEFAULT_LINKS, ...links };
  const call = (path, options) => api(apiBase, path, options);
  const { usd, price: planPrice, charged } = useUsd(apiBase); // plan prices in US$ for visitors outside India, charged in ₹

  const [step, setStep] = useState('name'); // name | details | code | plan | ready | awaiting
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Step 1: business type and store name
  const [businessType, setBusinessType] = useState(category);
  const [shopName, setShopName] = useState('');
  const [availability, setAvailability] = useState(null); // { slug, available, message, subdomain, unchecked }
  const [checkingName, setCheckingName] = useState(false);

  // Steps 2-3: contact and email code
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpNote, setOtpNote] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [offerUsed, setOfferUsed] = useState(false);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const resend = useCountdown(30);

  // Step 4: plan, address, coupon, payment
  const [planList, setPlanList] = useState([]);
  const [plan, setPlan] = useState('trial');
  const [domainType, setDomainType] = useState('subdomain');
  const [domainQuery, setDomainQuery] = useState('');
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [exactMatch, setExactMatch] = useState(null);
  const [domainOptions, setDomainOptions] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [isSelfDomain, setIsSelfDomain] = useState(false);
  const [customDomain, setCustomDomain] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [purchase, setPurchase] = useState(null); // { id, payToken, status, amount, plan }
  const [paying, setPaying] = useState(false);
  const checkoutOpen = useRef(false);
  const readyWaiting = useRef(false);

  // Step 5: the created store
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const effectiveTemplate = resolveTemplate ? resolveTemplate(businessType, template) : template;
  const slug = slugify(shopName);
  const nameOk = Boolean(availability && availability.slug === slug && availability.available);

  // Opening: a finished sign-up starts over; otherwise the visitor continues where they were
  useEffect(() => {
    if (!open) return;
    onTrack('modal_open', 'Opened free trial sign-up', { template: effectiveTemplate });
    if (result) {
      setResult(null);
      setPurchase(null);
      setShopName('');
      setAvailability(null);
    }
    setStep('name');
    setError('');
    setCopied('');
    setBusinessType(category);
    call('/onboarding/plans')
      .then((data) => {
        const plans = Array.isArray(data.plans) ? data.plans : [];
        setPlanList(plans);
        const keys = plans.map((p) => p.key);
        const trial = plans.find((p) => p.isTrial);
        if (initialPlan && keys.includes(initialPlan)) setPlan(initialPlan);
        else if (trial) setPlan(trial.key);
        else if (keys.includes(data.defaultPlan)) setPlan(data.defaultPlan);
        else if (plans[0]) setPlan(plans[0].key);
      })
      .catch((err) => setError(err.message));
  }, [open]);

  // Lock the page behind the dialog (html, since body overflow does not reach the viewport when html clips)
  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    const prev = root.style.overflowY;
    root.style.overflowY = 'hidden';
    return () => {
      root.style.overflowY = prev;
    };
  }, [open]);

  // Escape closes the sign-up
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // The store address is checked half a second after typing stops; an answer for an older name is ignored
  useEffect(() => {
    if (!open || slug.length < 3) {
      setCheckingName(false);
      return undefined;
    }
    let live = true;
    setCheckingName(true);
    const timer = setTimeout(async () => {
      try {
        const data = await call(`/onboarding/slug/${encodeURIComponent(slug)}`);
        if (live) setAvailability({ slug, available: Boolean(data.available), message: data.message, subdomain: data.subdomain });
      } catch {
        // The check itself failed: let the visitor go on, the server checks the name again when creating
        if (live) setAvailability({ slug, available: true, unchecked: true });
      } finally {
        if (live) setCheckingName(false);
      }
    }, 450);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [open, slug]);

  // The free trial includes a subdomain only
  useEffect(() => {
    if (plan === 'trial') setDomainType('subdomain');
    setCoupon(null);
    setCouponError('');
  }, [plan]);

  // Main domain search (GoDaddy availability and yearly price)
  useEffect(() => {
    if (!open || domainType !== 'main') return undefined;
    const q = domainQuery.trim();
    if (q.length < 2) {
      setExactMatch(null);
      setDomainOptions([]);
      return undefined;
    }
    let live = true;
    setCheckingDomain(true);
    const timer = setTimeout(async () => {
      try {
        const data = await call(`/onboarding/domains?query=${encodeURIComponent(q)}`);
        if (!live) return;
        const domains = Array.isArray(data.domains) ? data.domains : [];
        setExactMatch(data.exactMatch || null);
        setDomainOptions(domains);
        setSelectedDomain((prev) => {
          if (isSelfDomain) return prev;
          if (data.exactMatch && data.exactMatch.available) return data.exactMatch.name;
          const firstFree = domains.find((d) => d.available);
          return firstFree ? firstFree.name : '';
        });
      } catch (err) {
        if (live) setError(err.message);
      } finally {
        if (live) setCheckingDomain(false);
      }
    }, 500);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [open, domainType, domainQuery]);

  // Prices
  const offerOf = (p) => (p && p.firstMonthOffer && !offerUsed ? p.firstMonthOffer.price : null);
  const chosenPlan = planList.find((p) => p.key === plan) || {};
  const isTrial = Boolean(chosenPlan.isTrial) || plan === 'trial';
  const planOffer = isTrial ? null : offerOf(chosenPlan);
  const couponOff = coupon ? coupon.discount : 0;
  const planCharge = isTrial ? 0 : (planOffer !== null ? planOffer : chosenPlan.price || 0) - couponOff;
  const planGst = Math.round((planCharge * (chosenPlan.gstPercent || 0)) / 100);
  const domainChoices = [
    ...(exactMatch && !domainOptions.some((d) => d.name === exactMatch.name) ? [exactMatch] : []),
    ...domainOptions,
  ];
  const selectedDomainObj = domainChoices.find((d) => d.name === selectedDomain);
  const domainCharge = domainType === 'main' && !isSelfDomain ? (selectedDomainObj && selectedDomainObj.price) || 0 : 0;
  const domainGst = domainCharge > 1 ? Math.round(domainCharge * 0.18) : 0; // not on the ₹1 test domain
  const total = planCharge + planGst + domainCharge + domainGst;
  const needsPayment = !isTrial || domainCharge > 0;

  // Always five steps; details and code show as done when an email verified earlier in this visit skips them
  const progressSteps = ['Store name', 'Your details', 'Verify', 'Plan', 'Ready'];
  const stepIndex = { name: 0, details: 1, code: 2, plan: 3, ready: 4, awaiting: 4 }[step];

  const run = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onTrack('modal_close', 'Closed free trial sign-up', { step, plan, domainType });
    onClose({ completed: step === 'ready' || step === 'awaiting', step });
  };

  // ---- Step 1 -> 2
  const continueFromName = () => {
    if (categoryOptions && !design && businessType.trim().length < 2) {
      document.getElementById('ozsf-business')?.focus();
      return setError('Tell us what you sell (e.g. Jewellery, Bakery, Sarees).');
    }
    if (slug.length < 3) return setError('Enter a store name with at least 3 letters or numbers.');
    if (checkingName || !availability || availability.slug !== slug) return setError('Checking your store name, one moment…');
    if (!nameOk) return setError(availability.message || 'That name is already taken. Try another name.');
    setError('');
    if (!domainQuery.trim()) setDomainQuery(shopName.trim());
    return setStep(verificationToken ? 'plan' : 'details');
  };

  // ---- Step 2 -> 3
  const sendCode = () => {
    if (!/^\d{10}$/.test(phone)) return setError('Enter a valid 10-digit WhatsApp number.');
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError('Enter a valid email address.');
    return run(async () => {
      const data = await call('/onboarding/otp/send', { method: 'POST', body: { phone, email: email.trim() } });
      setDevOtp(data.devOtp || '');
      setOtpNote(data.delivered === false && data.message ? data.message : `We emailed a 6-digit code to ${email.trim()}.`);
      setOtp('');
      setStep('code');
      resend.start();
    });
  };

  const resendCode = () =>
    run(async () => {
      const data = await call('/onboarding/otp/send', { method: 'POST', body: { phone, email: email.trim() } });
      setDevOtp(data.devOtp || '');
      setOtpNote(`We emailed a new code to ${email.trim()}.`);
      resend.start();
    });

  // ---- Step 3 -> 4
  const verifyCode = () => {
    if (!/^\d{6}$/.test(otp)) return setError('Enter the 6-digit code from your email.');
    return run(async () => {
      const data = await call('/onboarding/otp/verify', { method: 'POST', body: { phone, email: email.trim(), otp } });
      setVerificationToken(data.verificationToken || '');
      setOfferUsed(Boolean(data.offerUsed || data.hasUsedIntroOffer));
      setHasUsedTrial(Boolean(data.hasUsedTrial));
      if (data.hasUsedTrial && plan === 'trial') {
        const firstPaid = planList.find((p) => !p.isTrial);
        if (firstPaid) setPlan(firstPaid.key);
      }
      onTrack('modal_otp_verified', 'Verified OTP', { phone, email: email.trim() });
      setStep('plan');
    });
  };

  const selectPlan = (p) => {
    if (p.isTrial && hasUsedTrial) return;
    setPlan(p.key);
    onTrack('modal_plan_selected', `Selected ${p.isTrial ? '7-Day Free Trial' : p.name}`, { plan: p.key, planName: p.name, price: p.price });
  };

  const selectDomainType = (type) => {
    setDomainType(type);
    setIsSelfDomain(false);
    setCustomDomain('');
    setSelectedDomain('');
    if (type === 'main' && !domainQuery.trim()) setDomainQuery(shopName.trim());
  };

  const applyCoupon = async () => {
    setApplyingCoupon(true);
    setCouponError('');
    try {
      const data = await call('/onboarding/coupon', { method: 'POST', body: { code: couponCode.trim(), plan, verificationToken } });
      setCoupon(data.coupon);
    } catch (err) {
      setCoupon(null);
      setCouponError(err.message);
    } finally {
      setApplyingCoupon(false);
    }
  };

  // ---- Payment (paid plan or domain): the store is created once the payment is confirmed
  const showReady = () => setStep('ready');
  const applyPayment = (payment) => {
    setPurchase((current) => current && { ...current, status: payment.status });
    if (payment.status === 'paid' && payment.store) {
      setResult((current) => (current && current.details ? current : { ...payment.store, paidPlan: { amount: payment.amount, plan: payment.plan } }));
      if (checkoutOpen.current) readyWaiting.current = true; // shown when Razorpay's window has closed
      else showReady();
    }
  };
  const checkoutClosed = () => {
    checkoutOpen.current = false;
    if (!readyWaiting.current) return;
    readyWaiting.current = false;
    setTimeout(showReady, 300);
  };
  const payHeaders = (payToken) => ({ 'x-pay-token': payToken });

  const pay = (checkout, paymentId, payToken) => {
    setPaying(true);
    checkoutOpen.current = true;
    return openRazorpay(checkout, {
      prefill: { contact: phone, email: email.trim(), ...(checkout.prefill || {}) },
      onPaid: async (paid) => {
        checkoutClosed();
        try {
          const data = await call(`/billing/payments/${paymentId}/confirm`, { method: 'POST', body: paid, headers: payHeaders(payToken) });
          applyPayment(data.payment);
        } catch (err) {
          setError(err.message); // the background check still picks up the payment
        }
        setPaying(false);
      },
      onClose: () => {
        checkoutClosed();
        setPaying(false);
      },
    }).catch((err) => {
      checkoutClosed();
      setError(err.message);
      setPaying(false);
    });
  };

  const payAgain = async () => {
    setError('');
    try {
      const data = await call(`/billing/payments/${purchase.id}/checkout`, { method: 'POST', body: {}, headers: payHeaders(purchase.payToken) });
      applyPayment(data.payment);
      if (data.checkout) pay(data.checkout, purchase.id, purchase.payToken);
    } catch (err) {
      setError(err.message);
    }
  };

  // While unpaid, check every few seconds: a payment finished in a UPI app is confirmed by Razorpay
  const purchasePending = Boolean(purchase && purchase.payToken && purchase.status === 'pending');
  useEffect(() => {
    if (!purchasePending) return undefined;
    const timer = setInterval(() => {
      call(`/billing/payments/${purchase.id}`, { headers: payHeaders(purchase.payToken) })
        .then((data) => data.payment && applyPayment(data.payment))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [purchasePending, purchase && purchase.id]);

  // ---- Step 4: create the store (free trial) or start the payment
  const create = () => {
    const finalDomain = isSelfDomain ? customDomain.trim().toLowerCase() : selectedDomain;
    if (domainType === 'main') {
      if (isSelfDomain && !DOMAIN_RE.test(finalDomain)) return setError('Enter a valid domain name you will buy yourself (e.g. yourstore.com).');
      if (!isSelfDomain && !selectedDomain) return setError("Select one of the suggested domains or choose 'I will buy the domain myself'.");
    }
    if (isTrial) onTrack('trial_continue_click', 'Clicked Start my free website', { plan: 'trial' });
    onTrack(
      'modal_domain_submitted',
      `Submitted ${domainType === 'main' ? (isSelfDomain ? 'own domain' : 'suggested domain') : 'subdomain'}: ${finalDomain || slug}`,
      { domainType, domain: finalDomain || slug, isSelfDomain, domainPrice: domainCharge, plan }
    );
    return run(async () => {
      try {
        const data = await call('/onboarding/stores', {
          method: 'POST',
          body: {
            verificationToken,
            shopName: shopName.trim(),
            plan,
            template: effectiveTemplate || undefined,
            layout: layout || undefined,
            ...(currency && currency !== 'INR' ? { currency, country } : {}),
            category: (businessType || (design && design.label) || '').slice(0, 60) || undefined,
            introOffer: planOffer !== null,
            domainType,
            ...(domainType === 'main'
              ? { selectedDomain: finalDomain, selfDomain: isSelfDomain, buyDomain: !isSelfDomain && domainCharge > 0, domainPrice: domainCharge }
              : {}),
            ...(referral ? { ref: referral } : {}),
            ...(coupon && !isTrial ? { couponCode: coupon.code } : {}),
          },
        });
        if (!data.payment) {
          setResult(data);
          setStep('ready');
          return;
        }
        setPurchase({ id: data.payment.id, payToken: data.checkout && data.checkout.payToken, status: data.payment.status, amount: data.payment.amount, plan: data.payment.plan });
        if (data.checkout) pay(data.checkout, data.payment.id, data.checkout.payToken);
        else {
          // Online payment is off: the Ownezzy team confirms the payment, then sends the details
          setResult({ awaitingConfirmation: true, payment: data.payment, requestedDomain: data.requestedDomain, selfDomain: Boolean(data.selfDomain) });
          setStep('awaiting');
        }
      } catch (err) {
        // The 30-minute email proof ran out: verify again
        if (err.status === 401) {
          setVerificationToken('');
          setStep('details');
        }
        throw err;
      }
    });
  };

  // ---- Ready screen helpers
  const details = result && result.details;
  const reqDomain = (result && (result.requestedDomain || (details && details.requestedDomain))) || '';
  const isSelf = Boolean(result && (result.selfDomain || (details && details.selfDomain)));
  const subUrl = (details && (details.subdomainUrl || details.storeUrl)) || '';
  const sentTo = (r) => [r.emailed && r.emailed.delivered && r.emailed.to, r.whatsapp && r.whatsapp.delivered && 'your WhatsApp'].filter(Boolean).join(' and ');
  const detailsText = () => {
    const lines = [`Store website: ${details.storeUrl}`, `Admin URL: ${details.adminUrl}`, `Admin ID: ${details.adminId}`, `Admin password: ${details.adminPassword || EXISTING_PASSWORD}`];
    if (isSelf) lines.push('', 'Please buy your own domain and contact us for domain linking.', `Use this subdomain store URL for now: ${subUrl}`);
    else if (reqDomain) lines.push('', `Use this subdomain store URL for now: ${subUrl}`, `Your subdomain will be linked to your main domain (${reqDomain}) within 24 hrs.`);
    return lines.join('\n');
  };
  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setError('');
    } catch {
      setError('Could not copy automatically. Please copy the details by hand.');
    }
  };
  const credentialRow = (label, value, key) => (
    <div className="ozsf-cred-row" key={key}>
      <span>{label}</span>
      <div>
        <b>{value}</b>
        <button type="button" onClick={() => copyText(value, key)}>{copied === key ? '✓ Copied' : 'Copy'}</button>
      </div>
    </div>
  );

  if (!open) return null;

  const termsNote = (
    <p className="ozsf-terms">
      By continuing you agree to Ownezzy&apos;s <a href={urls.terms} target="_blank" rel="noopener noreferrer">Terms</a> and{' '}
      <a href={urls.privacy} target="_blank" rel="noopener noreferrer">Privacy Policy</a>. Paid plans are not refundable (<a href={urls.refund} target="_blank" rel="noopener noreferrer">Refund Policy</a>).
      {usd(1) && ' Plan prices are shown in US$ for you; payment is charged in Indian Rupees (₹) and your bank converts it, so the US$ amounts are estimates.'}
    </p>
  );

  const planRows = planList.map((p) => {
    const offer = p.isTrial ? null : offerOf(p);
    const disabled = Boolean(p.isTrial && hasUsedTrial);
    const priceText = p.isTrial ? planPrice(0) : offer !== null ? planPrice(offer) : planPrice(p.price);
    const unit = p.isTrial ? `${p.trialDays || 7} days free` : offer !== null ? 'first month' : p.periodMonths === 12 ? '/ year' : '/ month';
    const hint = p.isTrial
      ? disabled
        ? 'Already used on your account'
        : 'No card needed • Free subdomain'
      : offer !== null
      ? `Then ${planPrice(p.price)}/month • Main domain available`
      : p.periodMonths === 12
      ? `About ${planPrice(Math.round(p.price / 12))}/month • Main domain available`
      : 'Pay monthly • Main domain available';
    return (
      <button
        type="button"
        key={p.key}
        disabled={disabled}
        className={`ozsf-plan ${plan === p.key ? 'selected' : ''} ${p.isTrial ? 'is-trial' : ''}`}
        onClick={() => selectPlan(p)}
      >
        <span className="ozsf-radio">{plan === p.key ? '✓' : ''}</span>
        <span className="ozsf-plan-main">
          <b>
            {p.isTrial ? '7-Day Free Trial' : p.name}
            {p.isTrial && !disabled && <em className="ozsf-tag">Recommended</em>}
            {offer !== null && <em className="ozsf-tag ozsf-tag-offer">Offer</em>}
          </b>
          <small>{hint}</small>
        </span>
        <span className="ozsf-plan-price">
          <strong>{priceText}</strong>
          <small>{unit}</small>
          {!p.isTrial && charged(offer !== null ? offer : p.price) && <small className="ozsf-usd">{charged(offer !== null ? offer : p.price)}</small>}
        </span>
      </button>
    );
  });

  return (
    <div className="ozsf-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <section className="ozsf-modal" role="dialog" aria-modal="true" aria-labelledby="ozsf-title">
        <button type="button" className="ozsf-close" onClick={close} aria-label="Close">✕</button>

        <div className="ozsf-head">
          {logo && <img className="ozsf-logo" src={logo} alt="Ownezzy" />}
          {step !== 'ready' && <span className="ozsf-pill">✨ 7-DAY FREE TRIAL • NO CREDIT CARD</span>}
          <h2 id="ozsf-title">{step === 'ready' ? '🎉 Your store is ready!' : 'Get your online store'}</h2>
          {/* The ready screen drops the progress bar so the login details fit without scrolling */}
          {step !== 'ready' && (
            <div className="ozsf-progress" aria-label={`Step ${stepIndex + 1} of ${progressSteps.length}`}>
              {progressSteps.map((label, i) => (
                <span key={label} className={i < stepIndex ? 'done' : i === stepIndex ? 'active' : ''}>
                  <i>{i < stepIndex ? '✓' : i + 1}</i>
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="ozsf-body">
          {error && <div className="ozsf-error" role="alert">⚠️ {error}</div>}

          {step === 'name' && (
            <>
              {design && (
                <div className="ozsf-design">
                  <span className="ozsf-design-icon" aria-hidden="true">{design.icon || '🛍️'}</span>
                  <span className="ozsf-design-text">
                    <small>Selected design</small>
                    <b>{design.name}{design.label ? ` • ${design.label}` : ''}</b>
                  </span>
                  {design.demoUrl && (
                    <a href={design.demoUrl} target="_blank" rel="noopener noreferrer">View demo ↗</a>
                  )}
                </div>
              )}

              {categoryOptions && !design && (
                <>
                  <label className="ozsf-label" htmlFor="ozsf-business">What do you sell? <span className="ozsf-required" aria-hidden="true">*</span></label>
                  <input
                    id="ozsf-business"
                    className="ozsf-input"
                    list="ozsf-business-list"
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    placeholder="e.g. Jewellery, Bakery, Sarees"
                    maxLength={60}
                    required
                    aria-required="true"
                    autoFocus
                  />
                  <datalist id="ozsf-business-list">
                    {categoryOptions.map((c) => (
                      <option key={c.name} value={c.name} />
                    ))}
                  </datalist>
                </>
              )}

              <label className="ozsf-label" htmlFor="ozsf-shop">Name your store <span className="ozsf-required" aria-hidden="true">*</span></label>
              <div className="ozsf-suffix-input">
                <input
                  id="ozsf-shop"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && continueFromName()}
                  placeholder="e.g. Kavya Boutique"
                  maxLength={50}
                  required
                  aria-required="true"
                  autoFocus={!(categoryOptions && !design)}
                />
                <span>.ownezzy.com</span>
              </div>
              {slug.length > 0 && slug.length < 3 && <div className="ozsf-status">Use at least 3 letters or numbers.</div>}
              {slug.length >= 3 && checkingName && <div className="ozsf-status"><Spinner /> Checking {slug}.ownezzy.com…</div>}
              {slug.length >= 3 && !checkingName && nameOk && (
                <div className="ozsf-status ok">✓ <b>{availability.subdomain || `${slug}.ownezzy.com`}</b> is available</div>
              )}
              {slug.length >= 3 && !checkingName && availability && availability.slug === slug && !availability.available && (
                <div className="ozsf-status taken">✕ {availability.message || 'That name is already taken. Try another name.'}</div>
              )}

              <button type="button" className="ozsf-primary" onClick={continueFromName}>Continue →</button>
              <p className="ozsf-note">Free for 7 days • No credit card • Live in 60 seconds</p>
            </>
          )}

          {step === 'details' && (
            <>
              <h3>Where should we send your login details?</h3>
              <label className="ozsf-label" htmlFor="ozsf-phone">WhatsApp number</label>
              <div className="ozsf-prefix-input">
                <span>🇮🇳 +91</span>
                <input
                  id="ozsf-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="10-digit mobile number"
                  autoFocus
                />
              </div>
              <label className="ozsf-label" htmlFor="ozsf-email">Email</label>
              <input
                id="ozsf-email"
                className="ozsf-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendCode()}
                autoComplete="email"
                inputMode="email"
                placeholder="you@gmail.com"
              />
              <button type="button" className="ozsf-primary" onClick={sendCode} disabled={busy}>
                {busy ? <><Spinner /> Sending code…</> : 'Email me a code →'}
              </button>
              <button type="button" className="ozsf-link" onClick={() => { setError(''); setStep('name'); }}>← Back</button>
            </>
          )}

          {step === 'code' && (
            <>
              <h3>Enter the 6-digit code</h3>
              <p className="ozsf-sub">{otpNote}</p>
              <input
                className="ozsf-input ozsf-otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => e.key === 'Enter' && verifyCode()}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="• • • • • •"
                maxLength={6}
                autoFocus
              />
              {process.env.NODE_ENV !== 'production' && devOtp && <p className="ozsf-note">Development only: your code is <b>{devOtp}</b>.</p>}
              <button type="button" className="ozsf-primary" onClick={verifyCode} disabled={busy}>
                {busy ? <><Spinner /> Verifying…</> : 'Verify & continue →'}
              </button>
              <p className="ozsf-note">Didn&apos;t get it? Check your Spam / Promotions folder.</p>
              <div className="ozsf-link-row">
                <button type="button" className="ozsf-link" onClick={resendCode} disabled={busy || resend.left > 0}>
                  {resend.left > 0 ? `Resend code in ${resend.left}s` : 'Resend code'}
                </button>
                <button type="button" className="ozsf-link" onClick={() => { setError(''); setStep('details'); }}>Change number or email</button>
              </div>
            </>
          )}

          {step === 'plan' && (
            <>
              <h3>Choose how to start</h3>
              {hasUsedTrial && <p className="ozsf-sub">Welcome back! Your account already used its free trial, so choose a plan for this new store.</p>}
              <div className="ozsf-plans">{planRows}</div>
              {offerUsed && planList.some((p) => p.firstMonthOffer) && (
                <p className="ozsf-note">The first-month offer was already used with your number or email, so Monthly is at its usual price.</p>
              )}

              <div className="ozsf-address">
                <span className="ozsf-label">Website address</span>
                {isTrial ? (
                  <div className="ozsf-info">✓ <b>{slug}.ownezzy.com</b> (free). Choose Monthly or Yearly for a main domain like yourshop.com.</div>
                ) : (
                  <>
                    <div className="ozsf-toggle">
                      <button type="button" className={domainType === 'subdomain' ? 'selected' : ''} onClick={() => selectDomainType('subdomain')}>
                        Free subdomain <b>₹0</b>
                      </button>
                      <button type="button" className={domainType === 'main' ? 'selected' : ''} onClick={() => selectDomainType('main')}>
                        Main domain <b>Yearly fee</b>
                      </button>
                    </div>
                    {domainType === 'subdomain' && <div className="ozsf-info">✓ <b>{slug}.ownezzy.com</b> (free)</div>}
                    {domainType === 'main' && (
                      <div className="ozsf-domains">
                        <div className="ozsf-suffix-input">
                          <input
                            value={domainQuery}
                            onChange={(e) => setDomainQuery(e.target.value)}
                            placeholder="e.g. sunrise bakery or sunrisebakery.com"
                            maxLength={63}
                            autoCapitalize="none"
                          />
                          <span>GoDaddy</span>
                        </div>
                        {checkingDomain && <div className="ozsf-status"><Spinner /> Checking domains…</div>}
                        {!checkingDomain && exactMatch && (
                          <div className={`ozsf-status ${exactMatch.available ? 'ok' : 'taken'}`}>
                            {exactMatch.available ? `✓ ${exactMatch.name} is available!` : `✕ ${exactMatch.name} is taken. Pick one below.`}
                          </div>
                        )}
                        {domainChoices.map((d) => {
                          const active = !isSelfDomain && selectedDomain === d.name;
                          return (
                            <button
                              type="button"
                              key={d.name}
                              disabled={!d.available}
                              className={`ozsf-option ${active ? 'selected' : ''}`}
                              onClick={() => {
                                setIsSelfDomain(false);
                                setSelectedDomain(d.name);
                              }}
                            >
                              <span className="ozsf-radio">{active ? '✓' : ''}</span>
                              <span className="ozsf-option-main">
                                <b>{d.name}</b>
                                <small>
                                  {!d.available
                                    ? 'Not available'
                                    : d.price === 1
                                    ? 'Testing domain • ₹1 only'
                                    : d.renewalPrice
                                    ? `Renews at ${money(d.renewalPrice)}/year • +18% GST`
                                    : 'Renews yearly • +18% GST'}
                                </small>
                              </span>
                              <strong>{money(d.price)}/yr</strong>
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          className={`ozsf-option ${isSelfDomain ? 'selected' : ''}`}
                          onClick={() => {
                            setIsSelfDomain(true);
                            setSelectedDomain('');
                          }}
                        >
                          <span className="ozsf-radio">{isSelfDomain ? '✓' : ''}</span>
                          <span className="ozsf-option-main">
                            <b>I will buy the domain myself</b>
                            <small>Buy from GoDaddy, Namecheap, etc. We link it to your store for free.</small>
                          </span>
                          <strong>₹0</strong>
                        </button>
                        {isSelfDomain && (
                          <input
                            className="ozsf-input"
                            value={customDomain}
                            onChange={(e) => setCustomDomain(e.target.value)}
                            placeholder="yourstore.com"
                            autoCapitalize="none"
                            spellCheck="false"
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {!isTrial && planOffer === null && !purchase && (
                <div className="ozsf-coupon">
                  {coupon ? (
                    <div className="ozsf-info">
                      🎟️ <b>{coupon.code}</b> applied • {money(coupon.discount)} off
                      <button type="button" className="ozsf-link" onClick={() => { setCoupon(null); setCouponCode(''); }}>Remove</button>
                    </div>
                  ) : (
                    <div className="ozsf-coupon-form">
                      <input
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          setCouponError('');
                        }}
                        placeholder="Have a coupon code?"
                        maxLength={30}
                        aria-label="Coupon code"
                      />
                      <button type="button" disabled={applyingCoupon || !couponCode.trim()} onClick={applyCoupon}>
                        {applyingCoupon ? <Spinner /> : 'Apply'}
                      </button>
                    </div>
                  )}
                  {couponError && <div className="ozsf-status taken">{couponError}</div>}
                </div>
              )}

              {needsPayment && (
                <div className="ozsf-summary">
                  {!isTrial && (
                    <span>
                      {chosenPlan.name || 'Plan'}
                      {planOffer !== null ? ' (first month)' : ''}
                      <b>{money(planCharge + couponOff)}</b>
                    </span>
                  )}
                  {couponOff > 0 && <span>Coupon<b>−{money(couponOff)}</b></span>}
                  {planGst > 0 && <span>Plan GST ({chosenPlan.gstPercent}%)<b>{money(planGst)}</b></span>}
                  {domainCharge > 0 && <span>Domain (1 year)<b>{money(domainCharge)}</b></span>}
                  {domainGst > 0 && <span>Domain GST (18%)<b>{money(domainGst)}</b></span>}
                  <strong>Pay now<b>{money(total)}{usd(total) && <small className="ozsf-usd"> {usd(total)}</small>}</b></strong>
                </div>
              )}

              {termsNote}

              {purchase ? (
                <>
                  {paying && <div className="ozsf-status"><Spinner /> Waiting for payment confirmation…</div>}
                  <button type="button" className="ozsf-primary" onClick={payAgain} disabled={paying}>
                    {paying ? <><Spinner /> Waiting for payment</> : `Pay ${money(purchase.amount)} now →`}
                  </button>
                </>
              ) : (
                <button type="button" className="ozsf-primary" onClick={create} disabled={busy || planList.length === 0}>
                  {busy ? <><Spinner /> {needsPayment ? 'Opening payment…' : 'Creating your store…'}</> : needsPayment ? `Pay ${money(total)} →` : '🚀 Start my free website'}
                </button>
              )}
              {!purchase && <button type="button" className="ozsf-link" onClick={() => { setError(''); setStep('name'); }}>← Change store name</button>}
            </>
          )}

          {step === 'awaiting' && result && (
            <>
              <h3>Order received</h3>
              <p className="ozsf-sub">
                Your payment of {money(result.payment.amount)} will be confirmed by the Ownezzy team. We then create your website and email the login details to {email.trim()}.
              </p>
              <button type="button" className="ozsf-primary" onClick={close}>Done</button>
            </>
          )}

          {step === 'ready' && details && (
            <>
              <div className="ozsf-celebrate" aria-hidden="true">
                {CONFETTI.map((piece, i) => <i key={i} style={piece} />)}
              </div>
              <p className="ozsf-sub ozsf-center">
                {details.adminPassword ? 'Save these details now. The password is shown only once.' : 'This store was added to your Ownezzy account. Sign in with your existing password.'}
              </p>
              <div className="ozsf-creds">
                {credentialRow('Store website', details.storeUrl, 'storeUrl')}
                {credentialRow('Admin panel', details.adminUrl, 'adminUrl')}
                {credentialRow('Admin ID', details.adminId, 'adminId')}
                {details.adminPassword ? (
                  credentialRow('Password', details.adminPassword, 'adminPassword')
                ) : (
                  <div className="ozsf-cred-row"><span>Password</span><div><b>{EXISTING_PASSWORD}</b></div></div>
                )}
              </div>
              {result.trialEndsAt && (
                <div className="ozsf-info">
                  ✓ Free trial until {new Date(result.trialEndsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}.
                </div>
              )}
              {result.paidPlan && <div className="ozsf-info">✓ Payment received: {money(result.paidPlan.amount)}. Your {result.paidPlan.plan} plan is active.</div>}
              {isSelf && <div className="ozsf-info">🌐 Buy your domain and contact us to link it. Use {subUrl} until then.</div>}
              {!isSelf && reqDomain && <div className="ozsf-info">🌐 {reqDomain} will be linked to your store within 24 hrs. Use {subUrl} until then.</div>}
              <div className={`ozsf-info ${sentTo(result) ? '' : 'warn'}`}>
                {sentTo(result) ? `✉️ We sent these details to ${sentTo(result)}.` : "✉️ We couldn't send these details. Please copy them now."}
              </div>
              <div className="ozsf-actions">
                <a
                  className="ozsf-whatsapp"
                  href={`https://wa.me/?text=${encodeURIComponent(`My Ownezzy store is ready!\n\n${detailsText()}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Save on WhatsApp
                </a>
                <button type="button" className="ozsf-secondary" onClick={() => copyText(detailsText(), 'all')}>
                  {copied === 'all' ? '✓ Copied' : 'Copy all'}
                </button>
              </div>
              <a className="ozsf-primary" href={details.adminUrl} target="_blank" rel="noopener noreferrer">Open my admin panel →</a>
              <button type="button" className="ozsf-link" onClick={close}>Close</button>
            </>
          )}
        </div>

        <div className="ozsf-foot">🔒 Secure setup • 0% commission • No hidden fees</div>
      </section>
    </div>
  );
}
